import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useMotionValue, animate } from "framer-motion";
import { ArrowLeft, Maximize2, Minimize2, Share2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const items = [
  {
    key: "tonnes_treated",
    en: "Tonnes of waste treated",
    hi: "उपचारित अपशिष्ट (टन)",
    accent: "text-bone",
  },
  {
    key: "co2e_avoided_tonnes",
    en: "tCO₂e avoided",
    hi: "tCO₂e से बचाव",
    accent: "text-bone",
  },
  {
    key: "odour_free_days",
    en: "Odour-free site-days",
    hi: "गंध-मुक्त साइट-दिवस",
    accent: "text-terracotta",
  },
  {
    key: "bio_culture_litres",
    en: "Litres of OED applied",
    hi: "OED (लीटर) प्रयुक्त",
    accent: "text-bone",
  },
  {
    key: "sites_active",
    en: "Active deployments",
    hi: "सक्रिय तैनातियाँ",
    accent: "text-bone",
  },
];

function BigNumber({ value }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  const prev = useRef(0);
  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.floor(v).toLocaleString("en-IN")),
    });
    prev.current = value;
    return controls.stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span className="tabular-nums">{display}</span>;
}

export default function ImpactWall() {
  const { t, lang, toggle } = useLang();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [scope, setScope] = useState("all");
  const [presets, setPresets] = useState([{ key: "all", label: "All deployments", kind: "all" }]);
  const wrapRef = useRef(null);

  useSeo({
    title: "Fresh Earth · Live Impact Wall",
    description: "Live tally of OED deployments — tonnes treated, tCO₂e avoided, odour-free days.",
    path: "/impact/wall",
  });

  useEffect(() => {
    axios
      .get(`${API}/impact/scopes`)
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => { /* keep default */ });
  }, []);

  const fetchStats = async (scopeKey = scope) => {
    try {
      const { data } = await axios.get(`${API}/impact/stats`, { params: { scope: scopeKey } });
      setStats(data);
      setError(false);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    fetchStats(scope);
    const poll = setInterval(() => fetchStats(scope), 30_000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Micro-tick between server refreshes for a living-numbers feel
  useEffect(() => {
    if (!stats) return;
    const iv = setInterval(() => {
      setStats((s) =>
        s
          ? {
              ...s,
              tonnes_treated: s.tonnes_treated + 1,
              co2e_avoided_tonnes: s.co2e_avoided_tonnes + 1,
              bio_culture_litres: s.bio_culture_litres + 2,
            }
          : s
      );
    }, 6000);
    return () => clearInterval(iv);
  }, [!!stats]);

  const toggleFullscreen = async () => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      try {
        await el.requestFullscreen();
        setIsFull(true);
      } catch { /* noop */ }
    } else {
      try {
        await document.exitFullscreen();
        setIsFull(false);
      } catch { /* noop */ }
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  return (
    <div
      ref={wrapRef}
      className="min-h-screen bg-forest text-bone relative overflow-hidden"
      data-testid="impact-wall"
    >
      {/* --- Video-loop background layer (CSS-animated organic pulses) --- */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" data-testid="wall-video-loop">
        <div className="wall-blob wall-blob-a" />
        <div className="wall-blob wall-blob-b" />
        <div className="wall-blob wall-blob-c" />
        <div className="wall-blob wall-blob-d" />
      </div>
      {/* grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* soft glow */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-moss/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-terracotta/10 blur-[100px] pointer-events-none" />

      {/* Top bar */}
      <div className="relative flex items-center justify-between px-8 md:px-14 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-bone/60 hover:text-bone transition-colors text-xs tracking-[0.2em] uppercase" data-testid="wall-back">
          <ArrowLeft size={14} /> {t("Back to site", "साइट पर वापस")}
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to={`/impact/share?scope=${scope}`}
            data-testid="wall-share-link"
            className="inline-flex items-center gap-2 rounded-full border border-bone/20 hover:border-bone/60 px-4 py-2 text-xs tracking-[0.18em] uppercase text-bone/80 hover:text-bone transition-colors"
          >
            <Share2 size={14} /> {t("Share", "साझा")}
          </Link>
          <button
            type="button"
            onClick={toggle}
            className="text-xs tracking-[0.2em] uppercase text-bone/60 hover:text-bone transition-colors"
            data-testid="wall-lang-toggle"
          >
            {lang === "hi" ? "EN" : "हिं"}
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="inline-flex items-center gap-2 rounded-full border border-bone/20 hover:border-bone/60 px-4 py-2 text-xs tracking-[0.18em] uppercase text-bone/80 hover:text-bone transition-colors"
            data-testid="wall-fullscreen"
          >
            {isFull ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {isFull ? t("Exit", "बाहर") : t("Present", "प्रदर्शन")}
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="relative px-8 md:px-14 pt-10 md:pt-16">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-2.5 h-2.5 rounded-full bg-terracotta animate-pulse" />
          <span className="text-xs tracking-[0.28em] uppercase font-semibold text-bone/60">
            {t("Live Impact · Fresh Earth", "प्रत्यक्ष प्रभाव · Fresh Earth")}
            {stats?.scope_label && scope !== "all" && (
              <span className="ml-3 text-bone/40">· {stats.scope_label}</span>
            )}
          </span>
        </div>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.98] text-bone max-w-6xl">
          {t(
            "OED at work — measured across active sites.",
            "OED कार्य में — सक्रिय स्थलों पर मापित।"
          )}
        </h1>

        {/* Scope chips */}
        <div className="mt-8 md:mt-10 -mx-2 overflow-x-auto pb-2" data-testid="wall-scope-chips">
          <div className="flex items-center gap-2 px-2">
            {presets.map((p) => {
              const active = p.key === scope;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setScope(p.key)}
                  data-testid={`wall-scope-${p.key}`}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-[11px] tracking-[0.16em] uppercase transition-colors border ${
                    active
                      ? "bg-bone text-forest border-bone"
                      : "border-bone/20 text-bone/70 hover:text-bone hover:border-bone/50"
                  }`}
                >
                  {p.kind === "region" ? "◆ " : p.kind === "city" ? "● " : ""}{p.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Big stat + supporting grid */}
      <div className="relative px-8 md:px-14 mt-12 md:mt-16 pb-16">
        {error ? (
          <div className="text-bone/60" data-testid="wall-error">
            {t("Live data temporarily unavailable.", "प्रत्यक्ष डेटा अस्थायी रूप से अनुपलब्ध।")}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Hero metric — Tonnes treated */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7 bg-bone/[0.04] border border-bone/10 rounded-[28px] p-10 md:p-14 backdrop-blur-sm"
              data-testid="wall-hero-metric"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase text-bone/50">
                {t(items[0].en, items[0].hi)}
              </div>
              <div className="mt-6 font-serif text-[110px] md:text-[180px] leading-[0.9] text-bone">
                <BigNumber value={stats ? stats.tonnes_treated : 0} />
              </div>
              <div className="mt-6 text-sm md:text-base text-bone/60 max-w-xl">
                {t(
                  "Cumulative tonnes of legacy and daily municipal + industrial waste processed with OED across live deployments.",
                  "OED के साथ सक्रिय तैनातियों पर संसाधित पुराने और दैनिक नगरीय + औद्योगिक अपशिष्ट का संचित योग।"
                )}
              </div>
            </motion.div>

            {/* Supporting metrics */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-5">
              {items.slice(1).map((it, i) => {
                const val = stats ? stats[it.key] : 0;
                return (
                  <motion.div
                    key={it.key}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.1 }}
                    className="bg-bone/[0.04] border border-bone/10 rounded-2xl p-6 md:p-8 flex flex-col"
                    data-testid={`wall-metric-${it.key}`}
                  >
                    <div className="text-[9px] tracking-[0.24em] uppercase text-bone/50">
                      {t(it.en, it.hi)}
                    </div>
                    <div className={`mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-none ${it.accent}`}>
                      <BigNumber value={val} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[10px] tracking-[0.22em] uppercase text-bone/40">
          <div>
            {stats
              ? `${t("Last synced", "अंतिम सिंक")} · ${new Date(stats.as_of).toLocaleString(
                  lang === "hi" ? "hi-IN" : "en-IN"
                )}`
              : t("Loading…", "लोड हो रहा है…")}
          </div>
          <div>fresh-earth.com · care@fresh-earth.com · +91 98995 34141</div>
        </div>
      </div>
    </div>
  );
}
